/**
 * Hooks for managing item milestones
 * Includes Zod validation, logging, and KPI tracking
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { logger } from '@/lib/logger';
import { kpiTracker } from '@/lib/kpiTracker';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';
import { 
  itemMilestoneSchema, 
  createItemMilestoneSchema, 
  updateItemMilestoneSchema,
  type ItemMilestone,
  type CreateItemMilestone,
  type UpdateItemMilestone
} from '@/schemas/milestone';
import { toast } from 'sonner';

// Fetch milestones for a single item
export const useItemMilestones = (itemId: string, options?: { enabled?: boolean }) => {
  // ✅ HARDCODED (2025-11-09): Always enabled
  const milestoneFeatureEnabled = true;

  return useQuery({
    queryKey: ['item-milestones', itemId],
    queryFn: async () => {
      const startTime = performance.now();
      
      logger.debug('useItemMilestones', 'Fetching milestones', { itemId });

      const { data, error } = await supabase
        .from('item_milestones' as any)
        .select('id, item_id, name, milestone_type, start_date, end_date, status, completion_percentage, notes, sort_order, created_at, updated_at, created_by, modified_by, assigned_user_id, planning_locked')
        .eq('item_id', itemId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (error) {
        logger.error('useItemMilestones', 'Failed to fetch milestones', { itemId, error });
        throw error;
      }

      // Validate with Zod
      const validatedData = data.map(item => itemMilestoneSchema.parse(item));

      const loadTime = performance.now() - startTime;
      kpiTracker.recordKPI('milestone_load_time', loadTime, {
        threshold: 200,
        context: { itemId, count: validatedData.length }
      });

      logger.debug('useItemMilestones', 'Milestones loaded', { 
        itemId, 
        count: validatedData.length, 
        loadTime: `${loadTime.toFixed(2)}ms` 
      });

      return validatedData;
    },
    enabled: milestoneFeatureEnabled && (options?.enabled !== false),
  });
};

// Fetch milestones for multiple items (batch)
export const useItemsMilestones = (itemIds: string[], options?: { enabled?: boolean }) => {
  // ✅ HARDCODED (2025-11-09): Always enabled
  const milestoneFeatureEnabled = true;

  return useQuery({
    queryKey: ['items-milestones', itemIds],
    queryFn: async () => {
      if (itemIds.length === 0) return [];

      const startTime = performance.now();
      
      logger.debug('useItemsMilestones', 'Fetching milestones for multiple items', { itemIdsCount: itemIds.length });

      const data = await chunkedInQuerySimple(
        supabase,
        'item_milestones' as any,
        'item_id',
        itemIds,
        'id, item_id, name, milestone_type, start_date, end_date, status, completion_percentage, notes, sort_order, created_at, updated_at, created_by, modified_by, assigned_user_id, planning_locked'
      );

      // Sort by sort_order after chunked query merges results (stable tiebreaker: created_at, id)
      const sortedData = data.sort((a: any, b: any) =>
        ((a.sort_order ?? 0) - (b.sort_order ?? 0))
        || String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
        || String(a.id ?? '').localeCompare(String(b.id ?? ''))
      );

      // Validate with Zod
      const validatedData = sortedData.map(item => itemMilestoneSchema.parse(item));

      const loadTime = performance.now() - startTime;
      const payloadSize = new Blob([JSON.stringify(validatedData)]).size / 1024; // KB

      kpiTracker.recordKPI('milestone_load_time', loadTime, {
        threshold: 200,
        context: { itemIdsCount: itemIds.length, count: validatedData.length }
      });

      kpiTracker.recordKPI('db_query_size', payloadSize, {
        threshold: 500,
        context: { queryName: 'useItemsMilestones', itemIdsCount: itemIds.length }
      });

      logger.debug('useItemsMilestones', 'Milestones loaded', { 
        itemIdsCount: itemIds.length,
        count: validatedData.length, 
        loadTime: `${loadTime.toFixed(2)}ms`,
        payloadSize: `${payloadSize.toFixed(2)}KB`
      });

      return validatedData;
    },
    enabled: milestoneFeatureEnabled && itemIds.length > 0 && (options?.enabled !== false),
  });
};

// Create milestone
export const useCreateMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (milestone: CreateItemMilestone) => {
      logger.debug('useCreateMilestone', 'Creating milestone', { milestone });

      // Validate with Zod
      const validatedData = createItemMilestoneSchema.parse(milestone);

      const { data, error } = await supabase
        .from('item_milestones' as any)
        .insert([validatedData as any])
        .select('id, item_id, name, milestone_type, start_date, end_date, status, completion_percentage, notes, sort_order, created_at, updated_at, created_by, modified_by, assigned_user_id, planning_locked')
        .single();

      if (error) {
        logger.error('useCreateMilestone', 'Failed to create milestone', { milestone, error });
        throw error;
      }

      const validatedResponse = itemMilestoneSchema.parse(data);

      logger.info('useCreateMilestone', 'Milestone created', { milestoneId: validatedResponse.id });

      return validatedResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['item-milestones', data.item_id] });
      queryClient.invalidateQueries({ queryKey: ['items-milestones'] });
      toast.success('Milestone created successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to create milestone', { description: error.message });
      kpiTracker.recordKPI('error_rate', 1, { threshold: 0.01, context: { operation: 'createMilestone' } });
    },
  });
};

// Update milestone
export const useUpdateMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateItemMilestone }) => {
      logger.debug('useUpdateMilestone', 'Updating milestone', { id, updates });

      // Validate with Zod
      const validatedUpdates = updateItemMilestoneSchema.parse(updates);

      const { data, error } = await supabase
        .from('item_milestones' as any)
        .update(validatedUpdates)
        .eq('id', id)
        .select('id, item_id, name, milestone_type, start_date, end_date, status, completion_percentage, notes, sort_order, created_at, updated_at, created_by, modified_by, assigned_user_id, planning_locked')
        .single();

      if (error) {
        logger.error('useUpdateMilestone', 'Failed to update milestone', { id, updates, error });
        throw error;
      }

      const validatedResponse = itemMilestoneSchema.parse(data);

      logger.info('useUpdateMilestone', 'Milestone updated', { milestoneId: id });

      return validatedResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['item-milestones', data.item_id] });
      queryClient.invalidateQueries({ queryKey: ['items-milestones'] });
      toast.success('Milestone updated successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to update milestone', { description: error.message });
      kpiTracker.recordKPI('error_rate', 1, { threshold: 0.01, context: { operation: 'updateMilestone' } });
    },
  });
};

// Delete milestone
export const useDeleteMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, itemId }: { id: string; itemId: string }) => {
      logger.debug('useDeleteMilestone', 'Deleting milestone', { id, itemId });

      const { error } = await supabase
        .from('item_milestones' as any)
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('useDeleteMilestone', 'Failed to delete milestone', { id, error });
        throw error;
      }

      logger.info('useDeleteMilestone', 'Milestone deleted', { id });

      return { id, itemId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['item-milestones', data.itemId] });
      queryClient.invalidateQueries({ queryKey: ['items-milestones'] });
      // Invalidate project detail so summary_milestones orphan cleanup is visible
      queryClient.invalidateQueries({ queryKey: ['project-detail'] });
      toast.success('Milestone deleted successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to delete milestone', { description: error.message });
      kpiTracker.recordKPI('error_rate', 1, { threshold: 0.01, context: { operation: 'deleteMilestone' } });
    },
  });
};
